import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { LINK_COMPLIANCE_CALLING_LEARN_MORE_LINK } from 'shared/constants/links'
import { Phone } from 'entities/Phone/model/Phone'
import { type Cnam } from 'entities/Compliance/model/OrganizationCnam/Cnam'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { VoiceIntegrityModalContent } from 'pages/settings/pages/compliance/pages/calling/ui/VoiceIntegrityModalContent/VoiceIntegrityModalContent'
import { CnamCallerIdModalContent } from 'pages/settings/pages/compliance/pages/calling/ui/CnamCallerIdModalContent/CnamCallerIdModalContent'
import { CnamCallerIdSummaryModalActions } from 'pages/settings/pages/compliance/pages/calling/ui/CnamCallerIdSummaryModalActions/CnamCallerIdSummaryModalActions'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { EstimatedResponseInfo } from 'pages/settings/pages/compliance/ui/EstimatedResponseInfo/EstimatedResponseInfo'
import { statusMap } from 'pages/settings/pages/compliance/pages/calling/constants'
import { type CallingPageStore } from './callingPageStore'

export class SummaryStore {
  private _modalId = nanoid()
  private _deleteModalId = nanoid()
  private _cnamNumbers: Phone[] = []

  constructor(private _callingPageStore: CallingPageStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get callingPageStore() {
    return this._callingPageStore
  }

  get cnamNumbers() {
    return this._cnamNumbers
  }

  learnMoreModalAction = () => {
    window.open(LINK_COMPLIANCE_CALLING_LEARN_MORE_LINK, '_blank')
  }

  moveToUpdateVoiceIntegrity = () => {
    modalStore.removeModal(this._modalId)

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableVoiceIntegrity}`,
    })
  }

  openVoiceIntegritySummaryModal = () => {
    const { organizationVoiceIntegrity } = this._entities.complianceStore
    const { registration_errors } = organizationVoiceIntegrity || {}

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      width: 540,
      paddingContent: '16px 24px 24px 24px',
      title: 'Voice Integrity',
      showCloseButton: false,
      ModalContent: () => (
        <VoiceIntegrityModalContent
          voiceIntegrity={organizationVoiceIntegrity}
          statusData={
            <EstimatedResponseInfo
              iconData={statusMap[organizationVoiceIntegrity?.status as keyof typeof statusMap]}
            />
          }
          errorMessage={registration_errors?.[0]?.message}
          learnMoreModalAction={this.learnMoreModalAction}
          updateModalAction={this.moveToUpdateVoiceIntegrity}
        />
      ),
    })
  }

  removeModals = () => {
    modalStore.removeModal(this._deleteModalId)
    modalStore.removeModal(this._modalId)
  }

  onDeleteCnamCallerId = (cnam: Cnam) => {
    this._entities.complianceStore.complianceVoiceServicesStore.cnamServices?.deleteItem(
      cnam,
      this.removeModals
    )
  }

  onDeleteCnamCallerIdModal = (cnam: Cnam) => {
    const { deleteLoading } =
      this._entities.complianceStore.complianceVoiceServicesStore.cnamServices || {}

    modalStore.addModal({
      id: this._deleteModalId,
      type: ModalTypeList.ALERT,
      width: 540,
      title: 'Delete Caller ID?',
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        disabled: deleteLoading,
        onAction: () => {
          this.onDeleteCnamCallerId(cnam)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.removeModal(this._deleteModalId)
        },
      },
    })
  }

  onEditCnamNumbers = (cnam: Cnam) => {
    this.removeModals()

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.editCnamNumbers}/${cnam.id}`,
    })
  }

  loadCnamNumbers = async (id: number) => {
    try {
      const { data } = await VoiceServicesApi.getCnamNumbers(id)

      this._cnamNumbers = data.map((item) => new Phone(item, this._entities))
    } catch (e) {
      logger.error(e)
    }
  }

  openCnamToUpdate = (cnam: Cnam) => {
    this.removeModals()

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${cnam.id}/${CallingRoutesSteps.enableCnamCallerId}`,
    })
  }

  openCnamCallerIdSummaryModal = (cnam: Cnam) => {
    this.loadCnamNumbers(cnam.id)

    const { canDelete, canEdit } = cnam

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      width: 540,
      paddingContent: '18px 24px 24px 24px',
      title: 'CNAM Caller ID',
      showCloseButton: false,
      onClose: () => {
        this._cnamNumbers = []
        this.removeModals()
      },
      ModalContentProps: {
        cnam,
        store: this,
      },
      ...(canDelete || canEdit ? { ModalActions: CnamCallerIdSummaryModalActions } : {}),
      ModalContent: CnamCallerIdModalContent,
    })
  }
}
